import { OnInit, Component, Input, Output, EventEmitter } from "@angular/core";
import { FormGroup, FormBuilder, Validators, FormArray } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { IAssessmentQuestion } from "./models/assessmentQuestion";
import { IAssessment } from "./models/assessment";
import { AssessmentService } from "./services/assessment.service";
import { GenericFormValidator } from "../../validators/genericFormArray.validator";
import { ValidationRules, ValidationMessages } from "../../models/validationMessage";
import { IAssessmentAnswer } from "./models/assessmentAnswer";
import { RouterLink, Router, ActivatedRoute } from "@angular/router";
import * as _ from "lodash";
import { ScrollToService } from "ng2-scroll-to-el";

enum AssessmentType {
  'pre',
  'post'
}

@Component({
  selector: "assessment",
  templateUrl: "./assessment.component.html",
  styleUrls: ["./assessment.component.css"]
})
export class AssessmentComponent implements OnInit {

  loading: boolean;

  @Input()
  assessmentType: AssessmentType;

  @Output()
  editing: EventEmitter<boolean> = new EventEmitter<boolean>();

  userCorrectAnswersCount: number = 0;
  totalQuestionsCount: number = 0;
  preAssessmentCorrectAnswersCount: number = 0;
  postAssessmentCorrectAnswersCount: number = 0;
  type: number = 0;
  assessmentId: string;
  isPreAssessmentCompleted: boolean = false;
  isPostAssessmentCompleted: boolean = false;
  reTakePostAssessment: boolean = true;
  preAssessmentScore: number;
  postAssessmentScore: number;
  preAssessmentsInCorrectQuestions: Array<any> = [];
  preAssessmentsInCorrectQuestionIds: Array<number> = [];
  preAssessments: Array<IAssessment> = [];
  postAssessments: Array<IAssessment> = [];
  assessmentForm: FormGroup;
  assessmentQuestions: Array<IAssessmentQuestion> = [];

  isSaving: boolean = false;

  // Use with the generic validation message class   
  displayMessage = new ValidationMessages();
  private genericValidator: GenericFormValidator;
  private validationRules = new ValidationRules();

  constructor(private fb: FormBuilder, private assessmentService: AssessmentService,
    private toastr: ToastrService, private route: ActivatedRoute, private scrollToService: ScrollToService, private router: Router) {

    this.validationRules.Messages = {
      Answers: {
        required: "All questions must be answered"
      }
    }
    this.genericValidator = new GenericFormValidator(this.validationRules);
  }

  ngOnInit(): void {
    this.assessmentId = this.route.snapshot.paramMap.get("assessmentId");
    var type = this.route.snapshot.paramMap.get("type");

    if (type != null) {
      this.type = +type;
    } else {
      //default to pre-assessment
      this.type = this.assessmentType;
    }

    //If we have an ID, pull the assessment
    if (this.assessmentId != null && this.assessmentId != "") {

      this.loading = true;
      this.getOnlyAssessmentAndQuestionsByAssessmentId(this.assessmentId);

    } else {

      //Just get questions
      this.loading = true;
      //Let parent component know that we are editing
      this.editing.emit(true);      

      this.assessmentService.getQuestions(this.type)
        .subscribe(result => {
          this.assessmentQuestions = result;
          this.resetForm();
        });
    }
  }

  resetForm(): void {
    this.assessmentForm = this.fb.group({
      Type: [this.type],
      Answers: this.fb.array([])
    });

    this.assessmentQuestions.forEach(x => {
      this.answers.push(this.buildQuestionModel(x));
    });

    this.loading = false;
  }

  save(): void {
    this.isSaving = true;
    this.genericValidator.touchAll(this.assessmentForm);

    if (this.assessmentForm.valid) {
      const assessment = Object.assign({} as IAssessment, this.assessmentForm.value);

      this.assessmentService.saveAssessment(assessment).subscribe(result => {
        if (result.success == true) {
          this.toastr.success("Assessment Saved!");
          
          this.editing.emit(false);
          
          var path = this.type == 0 ? "preassessment" : "postassessment";
          this.router.navigate(["/employment101/" + path+"/" + result.id + "/" + this.type ]);

        }
        this.isSaving = false;
      }, error => {
        console.log(error);
        this.toastr.error("Error saving assessment");
        this.isSaving = false;

      });
    } else {
      //Else not all questions have been answered. Scroll to first question not answered
      var firstError = this.genericValidator.firstError(this.assessmentForm, "Answers");
      this.scrollToService.scrollTo("label[id='" + firstError + "']");
      this.toastr.error("Please make sure all questions have been answered.");
      this.isSaving = false;
    }
  }

  get answers(): FormArray {
    return <FormArray>this.assessmentForm.get("Answers");
  }

  buildQuestionModel(question: IAssessmentQuestion): FormGroup {
    var sortedAnswerOption = _.orderBy(question.answerOptions, ["value"], ["asc"]);
    return this.fb.group({
      QuestionNumber: [question.order, Validators.required],
      QuestionId: [question.id, Validators.required],
      Question: [question.question],
      SelectedAnswerId: [question.selectedAnswerId],
      ExplanationUrl: [question.explanationUrl],      
      Answer: [null, Validators.required],
      AnswerOptions: [sortedAnswerOption]
    });
  }

  getOnlyAssessment() {
    this.assessmentService.getOnlyAssessment().subscribe(result => {
      if (result != null) {
        if (this.type == 0) {
          this.preAssessments = result.filter(item => item.type === 0);
          if (this.preAssessments != null && this.preAssessments.length > 0) {
            this.preAssessmentScore = this.preAssessments[0].score;
            this.isPreAssessmentCompleted = true;
            if (this.assessmentType === AssessmentType.pre) {
              console.log("Set edit false - Pre");
              this.editing.emit(false);
            }
          }
        }
        if (this.type == 1) {
          this.postAssessments = result.filter(item => item.type === 1);
          if (this.postAssessments != null && this.postAssessments.length > 0) {
            this.isPostAssessmentCompleted = true;
          }
        }
      }
    });
  }


  /// Loads assessment info, questions, and responses
  getOnlyAssessmentAndQuestionsByAssessmentId(assessmentId) {
    this.assessmentService.getOnlyAssessmentByAssessmentId(assessmentId).subscribe(result => {
      if (result != null) {
        if (this.type == 0) {
          this.preAssessmentScore = result.score;
          this.isPreAssessmentCompleted = true;
        }
        if (this.type == 1) {
          this.postAssessmentScore = result.score;
          this.reTakePostAssessment = false;
          this.isPostAssessmentCompleted = true;
        }
      }
    });

    this.assessmentService.getQuestionsByAssessmentId(Number(assessmentId)).subscribe(result => {
      this.totalQuestionsCount = result.length;


      this.assessmentQuestions = [];

      if (this.type == 0) {
        //pre-assessment
        for (var i = 0; i < result.length; i++) {
          var preAssessmentSelectedId = result[i].selectedAnswerId;
          var preAssessmentAnswerOptions = result[i].answerOptions;
          var preAssessmentCorrectAnswerDetails = preAssessmentAnswerOptions.filter(item => item.correctAnswer)[0];

          if (preAssessmentCorrectAnswerDetails.id != preAssessmentSelectedId) {
            //this.preAssessmentsInCorrectQuestionIds.push(preAssessmentCorrectAnswerDetails.questionId);
            this.assessmentQuestions.push(result[i]);
          } else {
            this.preAssessmentCorrectAnswersCount = this.preAssessmentCorrectAnswersCount + 1;
          }

        }

        this.isPreAssessmentCompleted = true;
        //this.assessmentQuestions = this.preAssessmentsInCorrectQuestions;
        this.resetForm();

      } else if (this.type == 1) {
        //post 
        for (var i = 0; i < result.length; i++) {

          var selectedId = result[i].selectedAnswerId;
          var answerOptions = result[i].answerOptions;

          var correctAnswerDetails = answerOptions.filter(item => item.correctAnswer)[0];
          if (correctAnswerDetails.id === selectedId) {
            this.postAssessmentCorrectAnswersCount = this.postAssessmentCorrectAnswersCount + 1;
          } else {
            this.assessmentQuestions.push(result[i]);
          }
        }

        //this.assessmentQuestions = result;
        this.resetForm();
      }
    });
  }

  goToPath(val: string) {

    this.router.navigate(["/employment101/guide/" + val]);

  }
}
